<script setup lang="ts">
import type { InteractionRequest } from 'eve/events'

const props = defineProps<{
  disabled?: boolean
  request: InteractionRequest
  interactionId: string
  response?: {
    optionId?: string
    text?: string
  }
}>()

const emit = defineEmits<{
  answer: [response: {
    optionId?: string
    interactionId: string
    text?: string
  }]
}>()

const { t } = useI18n()

const isAnswered = computed(() =>
  props.response?.optionId !== undefined
  || !!props.response?.text?.trim(),
)

const hasOptions = computed(() => (props.request.options?.length ?? 0) > 0)

const allowsFreeform = computed(() =>
  props.request.allowFreeform
  || props.request.display === 'text'
  || !hasOptions.value,
)

function isSelectedOption(optionId: string) {
  return props.response?.optionId === optionId
}

function answerWithOption(optionId: string) {
  if (props.disabled || isAnswered.value)
    return

  emit('answer', {
    optionId,
    interactionId: props.interactionId,
  })
}

const freeformAnswer = computed(() => {
  const text = props.response?.text?.trim()
  if (!text)
    return undefined

  // Option clicks only send optionId — show typed answers separately.
  if (props.response?.optionId) {
    const selectedLabel = props.request.options?.find(option => option.id === props.response?.optionId)?.label
    if (selectedLabel === text)
      return undefined
  }

  return text
})
</script>

<template>
  <div class="space-y-3.5">
    <p
      :id="`docue-agent-question-${interactionId}`"
      class="whitespace-pre-wrap text-sm font-medium leading-5 text-neutral-900"
    >
      {{ request.prompt }}
    </p>

    <div
      v-if="hasOptions"
      class="overflow-hidden rounded-lg border border-neutral-200"
      role="group"
      :aria-labelledby="`docue-agent-question-${interactionId}`"
    >
      <button
        v-for="option in request.options"
        :key="option.id"
        type="button"
        :disabled="disabled || isAnswered"
        :aria-pressed="isSelectedOption(option.id)"
        class="
          flex min-h-11 w-full items-start gap-3 border-b border-neutral-200
          px-3 py-2.5 text-start text-sm leading-5 last:border-b-0
          enabled:hover:bg-neutral-50
          enabled:focus-visible:bg-neutral-50 enabled:focus-visible:outline-none
          disabled:cursor-default
        "
        :class="[
          isSelectedOption(option.id) && 'bg-neutral-50',
          isAnswered && !isSelectedOption(option.id) && 'opacity-60',
          option.style === 'danger' && 'text-red-700',
          option.style === 'primary' && 'font-medium text-blue-700',
          option.style !== 'danger' && option.style !== 'primary' && 'text-neutral-800',
        ]"
        @click="answerWithOption(option.id)"
      >
        <span class="min-w-0 flex-1">
          <span class="block">{{ option.label }}</span>
          <span
            v-if="option.description"
            class="mt-0.5 block text-xs font-normal leading-4 text-neutral-500"
          >
            {{ option.description }}
          </span>
        </span>
        <UiIcon
          v-if="isSelectedOption(option.id)"
          name="docue:check"
          class="mt-0.5 size-4 shrink-0 text-neutral-700"
        />
      </button>
    </div>

    <div
      v-if="freeformAnswer"
      class="
        flex items-start gap-2 rounded-lg bg-neutral-100 px-3 py-2.5 text-sm
        leading-5 text-neutral-800
      "
    >
      <UiIcon
        name="docue:check"
        class="mt-0.5 size-4 shrink-0"
      />
      <p class="whitespace-pre-wrap">
        {{ freeformAnswer }}
      </p>
    </div>

    <p
      v-else-if="allowsFreeform && !isAnswered"
      class="rounded-lg bg-neutral-50 px-3 py-2.5 text-xs leading-4 text-neutral-500"
    >
      {{ hasOptions ? t('docueAgent.question.orTypeAnswer') : t('docueAgent.question.typeAnswer') }}
    </p>
  </div>
</template>
